import { useLocalStorage } from 'usehooks-ts'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CalendarView } from './calendar/CalendarView'
import { ChatInterface } from './ai-chat/ChatInterface'
import { ChatContextProvider } from './ai-chat/ChatContextProvider'
import { useIsMobile } from '../../hooks/useIsMobile'

type ViewType = 'calendar' | 'chat'

export function RightSidebarWrapper({
  showSidebar: _showSidebar,
  toggleSidebar,
}: {
  showSidebar: boolean
  toggleSidebar: () => void
}) {
  const isMobile = useIsMobile()
  const [activeView, setActiveView] = useLocalStorage<ViewType>(
    'rightSidebarView',
    'calendar'
  )
  const [showCalendar, setShowCalendar] = useLocalStorage('showCalendar', true)

  // #region resizing
  // Manage resizing the right sidebar with useLocalStorage
  const sidebarReference = useRef<HTMLDivElement>(null)
  const isResizing = useRef<boolean>(false)
  const [sidebarWidth, setSidebarWidth] = useLocalStorage(
    'rightSidebarWidth',
    '300px'
  )
  const [isCurrentlyResizing, setIsCurrentlyResizing] = useState(false)

  // Apply the saved width from localStorage when component mounts or width changes
  useEffect(() => {
    if (sidebarReference.current && !isResizing.current) {
      sidebarReference.current.style.width = sidebarWidth
    }
  }, [sidebarWidth])

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    isResizing.current = true
    setIsCurrentlyResizing(true)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = useCallback((event: globalThis.MouseEvent) => {
    if (!isResizing.current) return

    event.preventDefault()

    const newWidth = Math.max(
      150,
      Math.min(
        1000,
        (sidebarReference.current?.getBoundingClientRect().right ?? 0) -
          event.clientX
      )
    )

    if (sidebarReference.current) {
      sidebarReference.current.style.width = `${newWidth.toString()}px`
    }
  }, [])

  const handleMouseUp = useCallback(
    (event: globalThis.MouseEvent) => {
      event.preventDefault()
      isResizing.current = false
      setIsCurrentlyResizing(false)

      if (sidebarReference.current) {
        setSidebarWidth(sidebarReference.current.style.width)
      }

      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    },
    [handleMouseMove, setSidebarWidth]
  )

  // Clean up event listeners when component unmounts
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])
  // #endregion

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar)
  }

  if (isMobile) {
    // On mobile, render calendar directly as before
    return (
      <CalendarView
        showCalendar={showCalendar}
        toggleCalendar={toggleCalendar}
        toggleSidebar={toggleSidebar}
      />
    )
  }

  return (
    <div className='sticky top-0 flex h-dvh max-h-dvh select-none items-center justify-center'>
      <div className='relative flex h-full flex-col items-center justify-center'>
        {/* Chevron for collapsing sidebar - centered */}
        <button
          type='button'
          onClick={toggleCalendar}
          className='-ml-6 bg-transparent py-4 opacity-25 transition hover:bg-gray-200 hover:opacity-100 dark:hover:bg-gray-700'
        >
          <i
            className={`fa-solid fa-chevron-${showCalendar ? 'right' : 'left'}`}
          />
        </button>

        {/* View toggle buttons at bottom */}
        {showCalendar && (
          <div className='absolute bottom-1 -ml-7 flex flex-col gap-0'>
            <button
              type='button'
              onClick={() => {
                setActiveView('calendar')
              }}
              className={classNames(
                'px-1 py-0 text-sm transition',
                activeView === 'calendar'
                  ? 'text-amber-600 opacity-100'
                  : 'bg-transparent opacity-25 hover:opacity-100'
              )}
              title='Calendar'
            >
              <i className='fa-regular fa-calendar' />
            </button>
            <button
              type='button'
              onClick={() => {
                setActiveView('chat')
              }}
              className={classNames(
                'px-1 py-1 text-sm transition',
                activeView === 'chat'
                  ? 'text-amber-600 opacity-100'
                  : 'bg-transparent opacity-25 hover:opacity-100'
              )}
              title='AI Chat'
            >
              <i className='fa-regular fa-comment' />
            </button>
          </div>
        )}
      </div>

      {/* Resize handle */}
      {showCalendar && (
        <div
          className={classNames(
            'absolute left-0 top-0 z-10 h-full w-[6px] cursor-ew-resize',
            'transition-colors duration-150',
            isCurrentlyResizing
              ? 'bg-blue-400 dark:bg-blue-600'
              : 'hover:bg-slate-200 hover:dark:bg-slate-400'
          )}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* Content area */}
      {showCalendar && (
        <div
          ref={sidebarReference}
          className={classNames('h-dvh overflow-hidden border-l-2', {
            'transition-all duration-200 ease-in-out': !isCurrentlyResizing,
          })}
        >
          {activeView === 'calendar' ? (
            <CalendarView
              showCalendar={true}
              toggleCalendar={() => {
                // Empty function for embedded view
              }}
              toggleSidebar={toggleSidebar}
              isEmbedded={true}
            />
          ) : (
            <ChatContextProvider>
              <ChatInterface />
            </ChatContextProvider>
          )}
        </div>
      )}
    </div>
  )
}
